<script setup>
/**
 * Setup — the page that makes the outbound API usable by the person who owns it.
 *
 * WHAT WAS TRUE BEFORE THIS SCREEN EXISTED: `outbound_settings.enabled` defaults to false
 * for every workspace, and there was no way to change it that did not involve an operator
 * with a psql prompt. The same went for pausing a runaway integration and for creating the
 * key without which nothing can send at all. So the master switch at the top is not one
 * more setting on a settings page — it is the whole of Phase 1 becoming reachable, and it
 * is why the "off" state says what is off in a sentence instead of leaving a grey toggle
 * to imply it.
 *
 * FIVE READS, ONE OF WHICH IS THE PAGE. The settings are the spine: without them there is
 * nothing to render, so their failure is the page's error state. The keys, the senders,
 * the workspace's inbox names and the approved messages each fail on their own and each
 * degrades in place — a list that says it could not be read, next to controls that still
 * work. Collapsing any of them into the page's error state would take a working master
 * switch off screen because a template list timed out.
 *
 * "SENDING IS NOT SWITCHED ON FOR THIS WORKSPACE" IS NOT AN ERROR. The sidecar answers 403
 * `outbound_not_provisioned` when the workspace has no WhatsApp number wired for sending,
 * and the BFF gives that code its own name and its own sentence. Rendered as a failure it
 * would tell an owner to check her connection about a connection that is fine — the same
 * defect `WASLO_STATE.NOT_PROVISIONED` exists to prevent one layer up. So it renders the
 * explanation as content, with no "Try again" button, because retrying cannot connect a
 * number — and it renders it through `OutboundNotSetUp`, the ONE component all three
 * outbound screens use, rather than through a card only this page had.
 *
 * NOT KEYED ON `FORBIDDEN`. It used to be, and that inference was correct only because
 * every route in this section happens to be admin-only — true today, not a contract. A
 * genuine 403 `forbidden` is a permission failure and now reads as one.
 *
 * EVERY WRITE IS PATCH-THEN-RENDER, WITH NO OPTIMISM ANYWHERE. The control is disabled,
 * the PATCH goes, and the page re-renders from the response — which is the SAVED state in
 * full, so a value the sidecar normalised (a test number reformatted to E.164) never
 * diverges on screen from what is stored. One `pendingField` for the whole settings
 * object rather than one flag per control, because the four writes all PATCH the same row
 * and letting two of them race would mean the last response wins over a field it never
 * carried.
 *
 * THE MINTED SECRET NEVER ENTERS THE LIST. `createOutboundKey` answers with the only copy
 * of the plaintext that will ever exist; this page pulls exactly two fields out of that
 * answer into `mintedKey`, hands them to `OutboundSecretPanel`, and re-reads the key list
 * from the server rather than pushing the minted object into it. The rows in that list are
 * built by a reader with no `secret` field at all, so there is no path by which the
 * credential can reach a row, a toast or a log line.
 */
import { computed, ref, watch } from 'vue';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';

import NewOutboundKeyDialog from '../../components/outbound/NewOutboundKeyDialog.vue';
import OutboundKeyRow from '../../components/outbound/OutboundKeyRow.vue';
import OutboundLimitsCard from '../../components/outbound/OutboundLimitsCard.vue';
import OutboundNotSetUp from '../../components/outbound/OutboundNotSetUp.vue';
import OutboundQuickstart from '../../components/outbound/OutboundQuickstart.vue';
import OutboundSecretPanel from '../../components/outbound/OutboundSecretPanel.vue';
import OutboundSwitchCard from '../../components/outbound/OutboundSwitchCard.vue';
import OutboundTemplatesCard from '../../components/outbound/OutboundTemplatesCard.vue';
import TestNumbersEditor from '../../components/outbound/TestNumbersEditor.vue';
import WasloExplainer from '../../components/WasloExplainer.vue';
import WasloPageLayout from '../../components/WasloPageLayout.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloApi } from '../../composables/useWasloApi.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { relativeTime } from '../../helpers/relative-time.js';
import { wasloApi, WASLO_ERROR_CODES } from '../../api/client.js';

const { t, locale } = useWasloI18n();
const accountId = useWasloAccountId();

// ── the reads ──────────────────────────────────────────────────────────────────────
// `isEmpty: () => false` on all of them: none of these screens has an "empty" meaning
// that the frame's empty branch would render better than the page can. A workspace with
// no keys is a workspace on its first day, and the card that says so is where the button
// to fix it lives.
const settings = useWasloApi(
  signal => wasloApi.getOutboundSettings(accountId.value, signal),
  { isEmpty: () => false }
);

const keys = useWasloApi(
  signal => wasloApi.getOutboundKeys(accountId.value, signal),
  { isEmpty: () => false }
);

const senders = useWasloApi(
  signal => wasloApi.getOutboundSenders(accountId.value, signal),
  { isEmpty: () => false }
);

// The workspace's own inbox names and numbers. The sender DTO carries an `inboxId` and
// deliberately nothing else — `botRef` is the signed callback path and has no business in
// a browser — so a sender is only nameable by joining it to this list.
const inboxes = useWasloApi(
  signal => wasloApi.getInboxes(accountId.value, signal),
  { isEmpty: () => false }
);

/** The sender the Messages section and the Quickstart are talking about. */
const chosenInboxId = ref('');

// `immediate: false`: templates are approved PER SENDER, so a list fetched before one is
// chosen is not a smaller answer but a wrong one.
const templates = useWasloApi(
  signal =>
    wasloApi.getOutboundTemplates(
      accountId.value,
      Number(chosenInboxId.value),
      signal
    ),
  { immediate: false, isEmpty: () => false }
);

watch(
  () => senders.data.value,
  rows => {
    if (chosenInboxId.value === '' && rows && rows.length > 0) {
      chosenInboxId.value = String(rows[0].inboxId);
    }
  }
);

watch(chosenInboxId, inboxId => {
  if (inboxId !== '') templates.reload();
});

/** F3.3: approved messages are not set up for this workspace yet. A state, not an error. */
const templatesNotSetUp = computed(
  () =>
    templates.error.value?.code === WASLO_ERROR_CODES.BROADCAST_NOT_PROVISIONED
);

// ── the page's own state ───────────────────────────────────────────────────────────
const current = computed(() => settings.data.value);

/**
 * The 403 that means "no WhatsApp number is wired for sending here". It outranks the
 * error branch and renders as content: nothing is broken and retrying cannot help.
 */
const isNotSetUp = computed(
  () =>
    settings.error.value?.code === WASLO_ERROR_CODES.OUTBOUND_NOT_PROVISIONED
);

const errorMessage = computed(() =>
  isNotSetUp.value ? '' : settings.errorMessage.value
);

const isFetching = settings.isFetching;

const inboxById = computed(() => {
  const map = new Map();
  (inboxes.data.value ?? []).forEach(inbox => map.set(inbox.id, inbox));
  return map;
});

/**
 * What to call one of this workspace's sending numbers.
 *
 * Three answers, and they are three different facts. A name we have is the name plus the
 * number (or the name plus "no number yet", which is a real state of a channel mid-setup).
 * An inbox we cannot find while the list itself loaded fine is a bot left behind by a
 * deleted channel — "a number that is no longer here", exactly. And a list we could not
 * READ is neither of those, so it says so rather than telling an owner her live number
 * has gone.
 */
const inboxLabel = inboxId => {
  const inbox = inboxById.value.get(inboxId);
  if (inbox) {
    return inbox.phoneNumber
      ? `${inbox.name} · ${inbox.phoneNumber}`
      : `${inbox.name} · ${t('WASLO.OUTBOUND.COMMON.NO_NUMBER')}`;
  }
  return inboxes.error.value
    ? t('WASLO.OUTBOUND.COMMON.UNAVAILABLE')
    : t('WASLO.OUTBOUND.COMMON.SENDER_UNKNOWN');
};

const senderOptions = computed(() =>
  (senders.data.value ?? []).map(sender => ({
    value: String(sender.inboxId),
    label: inboxLabel(sender.inboxId),
  }))
);

// ── writing the four settings fields ───────────────────────────────────────────────
const pendingField = ref(null);
const fieldErrors = ref({});
/**
 * Bumped when a switch write fails. `Switch` flips its own copy on click whether or not
 * the server agreed, so remounting it is the one reliable way to snap it back to what is
 * actually stored — the same fix, for the same reason, as `PermissionCard`.
 */
const switchEpoch = ref(0);

const setFieldError = (field, message) => {
  fieldErrors.value = { ...fieldErrors.value, [field]: message };
};

const clearFieldError = field => {
  if (fieldErrors.value[field]) {
    const next = { ...fieldErrors.value };
    delete next[field];
    fieldErrors.value = next;
  }
};

/**
 * The failure sentence is passed in ALREADY TRANSLATED rather than as a key: this is the
 * one place on the page where the message varies by caller, and a `t(someVariable)` is
 * exactly the dynamic lookup that makes a missing string invisible to tooling.
 */
const patchSettings = async (field, patch, failureMessage) => {
  if (pendingField.value !== null) return;
  pendingField.value = field;
  clearFieldError(field);
  try {
    settings.data.value = await wasloApi.updateOutboundSettings(
      accountId.value,
      patch
    );
  } catch {
    setFieldError(field, failureMessage);
    switchEpoch.value += 1;
  } finally {
    pendingField.value = null;
  }
};

const setEnabled = next =>
  patchSettings(
    'enabled',
    { enabled: next },
    t('WASLO.OUTBOUND.SETUP.ENABLE.FAILED')
  );

const setPaused = next =>
  patchSettings(
    'paused',
    { paused: next },
    t('WASLO.OUTBOUND.SETUP.PAUSE.FAILED')
  );

const setTestMode = next =>
  patchSettings(
    'testMode',
    { testMode: next },
    t('WASLO.OUTBOUND.SETUP.TEST.FAILED')
  );

// The whole list is sent every time, because that is what the field is: `testNumbers` is
// one JSON column, not a collection with its own add and remove routes.
const addTestNumber = number =>
  patchSettings(
    'testNumbers',
    { testNumbers: [...(current.value?.testNumbers ?? []), number] },
    t('WASLO.OUTBOUND.SETUP.TEST.FAILED')
  );

const removeTestNumber = number =>
  patchSettings(
    'testNumbers',
    {
      testNumbers: (current.value?.testNumbers ?? []).filter(
        entry => entry !== number
      ),
    },
    t('WASLO.OUTBOUND.SETUP.TEST.FAILED')
  );

// ── the pause banner ───────────────────────────────────────────────────────────────
/**
 * Who stopped the sending, and when. Four sentences rather than one with holes in it: a
 * workspace whose calls are being refused has to be able to read this, and "Paused by ,
 * ." is not a sentence. Both fields are null until somebody pauses, and either can be
 * null afterwards.
 */
const pausedLine = computed(() => {
  const data = current.value;
  if (!data || !data.paused) return '';
  const who = data.pausedBy;
  const when =
    data.pausedAt === null
      ? null
      : relativeTime(data.pausedAt, locale.value, {
          justNow: t('WASLO.OUTBOUND.COMMON.JUST_NOW'),
        });

  if (who && when)
    return t('WASLO.OUTBOUND.SETUP.PAUSE.PAUSED_BY', { who, when });
  if (when) return t('WASLO.OUTBOUND.SETUP.PAUSE.PAUSED_BY_UNKNOWN', { when });
  if (who) return t('WASLO.OUTBOUND.SETUP.PAUSE.PAUSED_WHEN_UNKNOWN', { who });
  return t('WASLO.OUTBOUND.SETUP.PAUSE.PAUSED_BARE');
});

// ── keys ───────────────────────────────────────────────────────────────────────────
const keyDialogRef = ref(null);
const secretDialogRef = ref(null);
const revokeDialogRef = ref(null);

const isCreatingKey = ref(false);
const createKeyError = ref('');
/** `{ secret, keyPrefix }` — two fields lifted out of the mint, and never the whole row. */
const mintedKey = ref(null);
const keyToRevoke = ref(null);
const isRevoking = ref(false);
const revokeError = ref('');

const keyRows = computed(() => keys.data.value ?? []);
const hasKey = computed(() =>
  keyRows.value.some(row => row.revokedAt === null)
);

const openKeyDialog = () => {
  createKeyError.value = '';
  keyDialogRef.value?.open();
};

const createKey = async input => {
  if (isCreatingKey.value) return;
  isCreatingKey.value = true;
  createKeyError.value = '';
  try {
    const minted = await wasloApi.createOutboundKey(accountId.value, {
      ...(input.label === '' ? {} : { label: input.label }),
      ...(input.inboxId === null ? {} : { inboxId: input.inboxId }),
    });
    keyDialogRef.value?.close();
    mintedKey.value = { secret: minted.secret, keyPrefix: minted.keyPrefix };
    secretDialogRef.value?.open();
    // Re-READ rather than push: the list is rendered from rows that structurally cannot
    // carry a secret, and this is what keeps it that way.
    keys.reload();
  } catch {
    createKeyError.value = t('WASLO.OUTBOUND.KEYS.NEW.FAILED');
  } finally {
    isCreatingKey.value = false;
  }
};

/**
 * Dropped the moment the panel is dismissed. The plaintext lives in one ref, for as long
 * as one dialog is open, and `@close` fires for the button, the backdrop and Escape alike.
 */
const forgetSecret = () => {
  mintedKey.value = null;
};

const askRevoke = row => {
  keyToRevoke.value = row;
  revokeError.value = '';
  revokeDialogRef.value?.open();
};

const revokeKey = async () => {
  const row = keyToRevoke.value;
  if (!row || isRevoking.value) return;
  isRevoking.value = true;
  revokeError.value = '';
  try {
    await wasloApi.revokeOutboundKey(accountId.value, row.id);
    revokeDialogRef.value?.close();
    keyToRevoke.value = null;
    useAlert(t('WASLO.OUTBOUND.KEYS.REVOKE.DONE'));
    keys.reload();
  } catch {
    revokeError.value = t('WASLO.OUTBOUND.KEYS.REVOKE.FAILED');
  } finally {
    isRevoking.value = false;
  }
};

// ── the approved messages, and the example built from one ──────────────────────────
const isSyncing = ref(false);
const syncNote = ref('');
const syncError = ref('');

/**
 * The echo check the DTO exists for: the BFF returns the `inboxId` it answered for, so a
 * list can be routed back to the sender that asked. `useWasloApi` already aborts an
 * in-flight read on re-entry, which makes this belt and braces — but the failure it
 * prevents is showing one number's approved wording under another number's name, and that
 * is not a smaller answer, it is a wrong one.
 */
const templateRows = computed(() => {
  const data = templates.data.value;
  if (!data || String(data.inboxId) !== chosenInboxId.value) return [];
  return data.templates;
});

/**
 * The message the example sends. An approved one by preference — the status is WhatsApp's
 * own word and anything else cannot actually be sent — and otherwise the first in the
 * list, because a name that is real is worth more in a code sample than a name that is
 * ideal.
 */
const quickstartTemplate = computed(
  () =>
    templateRows.value.find(row => row.status.toLowerCase() === 'approved') ??
    templateRows.value[0] ??
    null
);

const refreshTemplates = async () => {
  if (isSyncing.value || chosenInboxId.value === '') return;
  isSyncing.value = true;
  syncNote.value = '';
  syncError.value = '';
  try {
    const result = await wasloApi.syncOutboundTemplates(
      accountId.value,
      Number(chosenInboxId.value)
    );
    // The server's own sentence, verbatim. The refresh is background work upstream and can
    // be refused there, so the list below is NOT re-read on the way back: a read taken a
    // moment after the request returns exactly what is already on screen and would read as
    // "the refresh did nothing".
    syncNote.value = result.note;
  } catch {
    syncError.value = t('WASLO.OUTBOUND.SETUP.TEMPLATES.REFRESH_FAILED');
  } finally {
    isSyncing.value = false;
  }
};

const reload = () => {
  settings.reload();
  keys.reload();
  senders.reload();
  inboxes.reload();
  if (chosenInboxId.value !== '') templates.reload();
};
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.OUTBOUND.SETUP.TITLE')"
    :is-fetching="isFetching"
    :not-provisioned="settings.isNotProvisioned.value"
    :error-message="errorMessage"
    :is-empty="isNotSetUp"
    @retry="reload"
  >
    <!--
      NOT AN ERROR, so no triangle and no "Try again": this workspace simply has no
      WhatsApp number wired for sending, and nothing on this page can change that. The
      explainer and every control go with it, because none of them can be read or written
      either — and the frame's empty branch is what takes them, so this page cannot end up
      showing the same fact in a different shape from the Log and the message detail.
    -->
    <template #emptyState>
      <OutboundNotSetUp />
    </template>

    <template #body>
      <div class="flex flex-col gap-8 pb-10" data-waslo-outbound-setup>
        <WasloExplainer
          icon="i-lucide-code-xml"
          :note="t('WASLO.OUTBOUND.SETUP.SPOTLIGHT')"
        />

        <template v-if="current">
          <!-- 1 · THE MASTER SWITCH. -->
          <OutboundSwitchCard
            :heading="t('WASLO.OUTBOUND.SETUP.ENABLE.HEADING')"
            :label="t('WASLO.OUTBOUND.SETUP.ENABLE.LABEL')"
            :model-value="current.enabled"
            :body="
              current.enabled
                ? t('WASLO.OUTBOUND.SETUP.ENABLE.ON_BODY')
                : t('WASLO.OUTBOUND.SETUP.ENABLE.OFF_BODY')
            "
            :is-saving="pendingField === 'enabled'"
            :switch-epoch="switchEpoch"
            :save-error="fieldErrors.enabled || ''"
            data-waslo-outbound-enable
            @update:model-value="setEnabled"
          />

          <!--
            2 · PAUSE, and only once there is something to pause. A pause switch over a
            workspace that cannot send anything is a control with no effect to have.
          -->
          <OutboundSwitchCard
            v-if="current.enabled"
            :heading="t('WASLO.OUTBOUND.SETUP.PAUSE.HEADING')"
            :label="t('WASLO.OUTBOUND.SETUP.PAUSE.LABEL')"
            :model-value="current.paused"
            :body="
              current.paused
                ? t('WASLO.OUTBOUND.SETUP.PAUSE.ON_BODY')
                : t('WASLO.OUTBOUND.SETUP.PAUSE.OFF_BODY')
            "
            :is-saving="pendingField === 'paused'"
            :switch-epoch="switchEpoch"
            :save-error="fieldErrors.paused || ''"
            data-waslo-outbound-pause
            @update:model-value="setPaused"
          >
            <!--
              WHO STOPPED IT AND WHEN. A workspace whose calls are being refused must be
              able to see this without asking us; the action is offered here as well as on
              the switch because this banner is where somebody who came looking for the
              answer is actually reading.
            -->
            <Banner
              v-if="current.paused"
              color="amber"
              :action-label="t('WASLO.OUTBOUND.SETUP.PAUSE.RESUME')"
              data-waslo-outbound-paused-by
              @action="setPaused(false)"
            >
              {{ pausedLine }}
            </Banner>
          </OutboundSwitchCard>

          <!-- 3 · TEST MODE, and the numbers it is allowed to reach. -->
          <OutboundSwitchCard
            :heading="t('WASLO.OUTBOUND.SETUP.TEST.HEADING')"
            :label="t('WASLO.OUTBOUND.SETUP.TEST.LABEL')"
            :model-value="current.testMode"
            :body="
              current.testMode
                ? t('WASLO.OUTBOUND.SETUP.TEST.ON_BODY')
                : t('WASLO.OUTBOUND.SETUP.TEST.OFF_BODY')
            "
            :is-saving="pendingField === 'testMode'"
            :switch-epoch="switchEpoch"
            :save-error="fieldErrors.testMode || ''"
            data-waslo-outbound-test-mode
            @update:model-value="setTestMode"
          >
            <span class="w-full h-px bg-n-weak" />
            <TestNumbersEditor
              :numbers="current.testNumbers"
              :max-numbers="current.limits.maxTestNumbers"
              :is-saving="pendingField === 'testNumbers'"
              :is-live="current.testMode"
              :save-error="fieldErrors.testNumbers || ''"
              @add="addTestNumber"
              @remove="removeTestNumber"
            />
          </OutboundSwitchCard>

          <!-- 4 · KEYS. -->
          <section class="flex flex-col gap-4" data-waslo-outbound-keys>
            <SettingsHeader
              :heading="t('WASLO.OUTBOUND.KEYS.HEADING')"
              :description="t('WASLO.OUTBOUND.KEYS.DESCRIPTION')"
            />

            <div
              v-if="keys.isFetching.value"
              class="flex items-center justify-center py-8 text-n-slate-11"
              data-waslo-outbound-keys-loading
            >
              <Spinner />
            </div>

            <p
              v-else-if="keys.hasError.value"
              class="mb-0 text-sm leading-6 text-n-ruby-9"
              data-waslo-outbound-keys-error
            >
              {{ t('WASLO.OUTBOUND.KEYS.UNAVAILABLE') }}
            </p>

            <!--
              The empty state carries the button, because "nothing can send until you
              create one" and the way to create one belong in the same place.
            -->
            <section
              v-else-if="keyRows.length === 0"
              class="flex flex-col items-start gap-3 p-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
              data-waslo-outbound-keys-empty
            >
              <h3 class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t('WASLO.OUTBOUND.KEYS.EMPTY.TITLE') }}
              </h3>
              <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
                {{ t('WASLO.OUTBOUND.KEYS.EMPTY.BODY') }}
              </p>
              <Button
                :label="t('WASLO.OUTBOUND.KEYS.EMPTY.ACTION')"
                icon="i-lucide-plus"
                variant="solid"
                color="blue"
                size="sm"
                data-waslo-outbound-keys-create-first
                @click="openKeyDialog"
              />
            </section>

            <template v-else>
              <ul class="flex flex-col gap-3 mb-0 list-none">
                <OutboundKeyRow
                  v-for="row in keyRows"
                  :key="row.id"
                  :key-row="row"
                  :sender-label="
                    row.inboxId === null ? '' : inboxLabel(row.inboxId)
                  "
                  :is-revoking="isRevoking && keyToRevoke?.id === row.id"
                  @revoke="askRevoke"
                />
              </ul>
              <div class="flex">
                <Button
                  :label="t('WASLO.OUTBOUND.KEYS.CREATE')"
                  icon="i-lucide-plus"
                  variant="faded"
                  color="slate"
                  size="sm"
                  data-waslo-outbound-keys-create
                  @click="openKeyDialog"
                />
              </div>
            </template>
          </section>

          <!-- 5 · THE APPROVED MESSAGES this workspace may send. -->
          <section class="flex flex-col gap-4">
            <SettingsHeader
              :heading="t('WASLO.OUTBOUND.SETUP.TEMPLATES.HEADING')"
              :description="t('WASLO.OUTBOUND.SETUP.TEMPLATES.DESCRIPTION')"
            />
            <OutboundTemplatesCard
              v-model="chosenInboxId"
              :sender-options="senderOptions"
              :templates="templateRows"
              :is-fetching="chosenInboxId !== '' && templates.isFetching.value"
              :has-error="templates.hasError.value && !templatesNotSetUp"
              :not-set-up="templatesNotSetUp"
              :is-syncing="isSyncing"
              :sync-note="syncNote"
              :sync-error="syncError"
              @refresh="refreshTemplates"
            />
          </section>

          <!-- 6 · THE FIRST CALL. -->
          <section class="flex flex-col gap-4">
            <SettingsHeader
              :heading="t('WASLO.OUTBOUND.SETUP.QUICKSTART.HEADING')"
              :description="t('WASLO.OUTBOUND.SETUP.QUICKSTART.DESCRIPTION')"
            />
            <OutboundQuickstart
              :template="quickstartTemplate"
              :has-key="hasKey"
            />
          </section>

          <!-- 7 · WHAT THE WORKSPACE IS ALLOWED. Read-only, and it says who set it. -->
          <section class="flex flex-col gap-4">
            <SettingsHeader
              :heading="t('WASLO.OUTBOUND.SETUP.LIMITS.HEADING')"
              :description="t('WASLO.OUTBOUND.SETUP.LIMITS.DESCRIPTION')"
            />
            <OutboundLimitsCard
              :limits="current.limits"
              :operator-set="current.operatorSet"
            />
          </section>
        </template>
      </div>
    </template>
  </WasloPageLayout>

  <NewOutboundKeyDialog
    ref="keyDialogRef"
    :sender-options="senderOptions"
    :senders-unavailable="senders.hasError.value"
    :is-creating="isCreatingKey"
    :create-error="createKeyError"
    @create="createKey"
  />

  <!--
    The secret's whole lifetime on screen. `showCancelButton` is false because there is
    nothing to cancel — the key exists either way — and `@close` fires for the button, the
    backdrop and Escape alike, which is what drops the plaintext.
  -->
  <Dialog
    ref="secretDialogRef"
    width="md"
    :title="t('WASLO.OUTBOUND.KEYS.SECRET.TITLE')"
    :confirm-button-label="t('WASLO.OUTBOUND.KEYS.SECRET.DONE')"
    :show-cancel-button="false"
    data-waslo-outbound-secret-dialog
    @confirm="secretDialogRef?.close()"
    @close="forgetSecret"
  >
    <OutboundSecretPanel
      v-if="mintedKey"
      :secret="mintedKey.secret"
      :key-prefix="mintedKey.keyPrefix"
    />
  </Dialog>

  <Dialog
    ref="revokeDialogRef"
    type="alert"
    :title="t('WASLO.OUTBOUND.KEYS.REVOKE.CONFIRM_TITLE')"
    :description="t('WASLO.OUTBOUND.KEYS.REVOKE.CONFIRM_BODY')"
    :confirm-button-label="t('WASLO.OUTBOUND.KEYS.REVOKE.CONFIRM_YES')"
    :cancel-button-label="t('WASLO.OUTBOUND.KEYS.REVOKE.CANCEL')"
    :is-loading="isRevoking"
    :disable-confirm-button="isRevoking"
    data-waslo-outbound-key-revoke-dialog
    @confirm="revokeKey"
  >
    <Banner
      v-if="revokeError"
      color="ruby"
      data-waslo-outbound-key-revoke-error
    >
      {{ revokeError }}
    </Banner>
  </Dialog>
</template>
